import { type CSSProperties } from 'react';

import { type HTMLArkProps, ark } from '@ark-ui/react';

import type { Scale3 } from '../../lib/types';
import { cn } from '../../lib/utils';

type DataListOrientation = 'horizontal' | 'vertical';
type DataListAlign = 'start' | 'center' | 'end' | 'baseline';

interface DataListRootProps extends HTMLArkProps<'dl'> {
	size?: Scale3;
	orientation?: DataListOrientation;
}

function DataListRoot({
	className,
	size = '2',
	orientation = 'horizontal',
	children,
	...props
}: DataListRootProps) {
	return (
		<ark.dl
			className={cn('pt-data-list', className)}
			data-size={size}
			data-orientation={orientation}
			{...props}
		>
			{children}
		</ark.dl>
	);
}

interface DataListItemProps extends HTMLArkProps<'div'> {
	align?: DataListAlign;
}

function DataListItem({ className, align, ...props }: DataListItemProps) {
	return <ark.div className={cn('pt-data-list-item', className)} data-align={align} {...props} />;
}

interface DataListLabelProps extends HTMLArkProps<'dt'> {
	minWidth?: CSSProperties['minWidth'];
	width?: CSSProperties['width'];
}

function DataListLabel({ className, minWidth, width, style, ...props }: DataListLabelProps) {
	return (
		<ark.dt
			className={cn('pt-data-list-label', className)}
			style={{ minWidth, width, ...style }}
			{...props}
		/>
	);
}

function DataListValue({ className, ...props }: HTMLArkProps<'dd'>) {
	return <ark.dd className={cn('pt-data-list-value', className)} {...props} />;
}

const DataList = Object.assign(DataListRoot, {
	Root: DataListRoot,
	Item: DataListItem,
	Label: DataListLabel,
	Value: DataListValue
});

export { DataList, type DataListRootProps, type DataListItemProps, type DataListLabelProps };
